import { createTheme } from '@mui/material';
import { createRivetedPanelSurface, createSteelSurface, liquidGlass } from './surfaces';

/** Classic phosphor terminal green. */
export const TERMINAL_GREEN = '#00ff41';
/** Dimmed green for secondary text. */
const DIM_GREEN = 'rgba(0, 255, 65, 0.68)';
/** Bright pale green used for chips. */
const PALE_GREEN = '#b9ffce';

const greenGlassButton = {
  ...liquidGlass({
    blur: 10,
    saturate: 140,
    bgColor: 'rgba(120, 220, 160, 0.10)',
    gradientPeak: 0.16,
    gradientMid: 50,
    borderColor: 'rgba(0, 255, 65, 0.30)',
    boxShadowLayers: [
      'inset 0 1px 0 rgba(255, 255, 255, 0.18)',
      'inset 0 0 14px -4px rgba(0, 255, 65, 0.12)',
      '0 4px 14px rgba(0, 0, 0, 0.45)',
    ],
  }),
};

// Holographic projection: translucent glowing glyphs with a soft light bloom
const holoGlow = {
  color: 'rgba(170, 255, 200, 0.92)',
  textShadow:
    '0 0 1px rgba(190, 255, 215, 0.9), ' +
    '0 0 7px rgba(0, 255, 65, 0.50), ' +
    '0 0 20px rgba(0, 255, 65, 0.25)',
};

// Headings get horizontal projection banding via background-clipped text
export const holoBanded = {
  backgroundImage:
    'repeating-linear-gradient(to bottom, ' +
    'rgba(0, 255, 65, 0.95) 0px, rgba(0, 255, 65, 0.95) 2px, ' +
    'rgba(190, 255, 220, 0.70) 2px, rgba(190, 255, 220, 0.70) 3px, ' +
    'rgba(0, 255, 65, 0.55) 3px, rgba(0, 255, 65, 0.55) 4px)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textShadow: '0 0 8px rgba(0, 255, 65, 0.45), 0 0 24px rgba(0, 255, 65, 0.20)',
};

/**
 * Stargrave "ship terminal" theme — black background, bright green text,
 * brushed-steel chrome, and riveted gunmetal panels.
 */
export const stargraveTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: TERMINAL_GREEN, contrastText: '#02120a' },
    secondary: { main: '#39d98a' },
    background: { default: '#000000', paper: '#0d1210' },
    text: { primary: TERMINAL_GREEN, secondary: DIM_GREEN },
    divider: 'rgba(0, 255, 65, 0.25)',
  },
  shape: { borderRadius: 4 },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: holoGlow,
        h4: holoBanded,
        h5: holoBanded,
        h6: holoBanded,
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': { height: '100%' },
        body: {
          backgroundColor: '#000000',
          // Dark overlay keeps terminal-green text readable over the artwork
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(/Stargrave.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundAttachment: 'fixed',
          color: TERMINAL_GREEN,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          ...createRivetedPanelSurface(17),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          ...createSteelSurface(5),
          borderRadius: 0,
          color: TERMINAL_GREEN,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...createSteelSurface(78),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          ...createSteelSurface(47),
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          ...createRivetedPanelSurface(61),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: PALE_GREEN,
          backgroundColor: 'rgba(185, 255, 206, 0.10)',
          border: '1px solid rgba(185, 255, 206, 0.45)',
          boxShadow: '0 0 8px rgba(90, 255, 150, 0.45)',
          // Wrap long labels on narrow screens instead of truncating
          height: 'auto',
          minHeight: 24,
          '& .MuiChip-label': { whiteSpace: 'normal' },
        },
        // Selected chips: solid terminal green with dark text
        colorPrimary: {
          backgroundColor: TERMINAL_GREEN,
          color: '#02120a',
          border: '1px solid rgba(0, 200, 50, 0.9)',
          boxShadow: '0 0 12px rgba(0, 255, 65, 0.55)',
          textShadow: 'none',
          '&:hover': {
            backgroundColor: '#2aff63',
          },
          '& .MuiChip-deleteIcon': {
            color: 'rgba(2, 18, 10, 0.55)',
            '&:hover': {
              color: 'rgba(2, 18, 10, 0.85)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          ...greenGlassButton,
        },
        containedPrimary: {
          color: TERMINAL_GREEN,
          backgroundColor: 'rgba(0, 255, 65, 0.10)',
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 65, 0.18)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          ...greenGlassButton,
          color: TERMINAL_GREEN,
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 65, 0.18)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.24)',
              '0 0 14px rgba(0, 255, 65, 0.35)',
              '0 4px 14px rgba(0, 0, 0, 0.45)',
            ].join(', '),
          },
        },
        primary: {
          backgroundColor: 'rgba(0, 255, 65, 0.12)',
          color: TERMINAL_GREEN,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...greenGlassButton,
          color: TERMINAL_GREEN,
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 65, 0.16)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.24)',
              '0 0 10px rgba(0, 255, 65, 0.30)',
            ].join(', '),
          },
          '&:active': {
            backgroundColor: 'rgba(0, 255, 65, 0.22)',
            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.45)',
          },
          '&.Mui-disabled': {
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            backgroundColor: 'transparent',
            border: '1px solid rgba(0, 255, 65, 0.15)',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: DIM_GREEN,
          '&.Mui-selected': {
            color: TERMINAL_GREEN,
            textShadow: '0 0 8px rgba(0, 255, 65, 0.45)',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          ...greenGlassButton,
          color: DIM_GREEN,
          '&.Mui-selected': {
            color: '#02150a',
            backgroundColor: TERMINAL_GREEN,
            backgroundImage: 'none',
            textShadow: 'none',
            fontWeight: 700,
            boxShadow: '0 0 10px rgba(0, 255, 65, 0.35)',
            '&:hover': {
              backgroundColor: '#33ff6a',
            },
          },
        },
      },
    },
  },
});
